<template>
  <div id="login-wrapper">
    <div class="login-content">
      <h1 class="main-title">
        {{ $t('login.title') }}
      </h1>

      <div class="login-container">
        <h2>{{ $t('login.heading') }}</h2>
        <form @submit.prevent="login">
          <div class="input-container">
            <label for="username">{{ $t('login.username') }}</label>
            <input type="text" id="username" autocomplete="current-username" v-model="username" required
              :disabled="isLoading">
          </div>
          <div class="input-container">
            <label for="password">{{ $t('login.password') }}</label>
            <input type="password" id="password" autocomplete="current-password" v-model="password" required
              :disabled="isLoading">
          </div>

          <button type="submit" :disabled="isLoading" :class="{ 'loading': isLoading }">
            <span v-if="!isLoading">{{ $t('login.submit') }}</span>
            <LoadingSpinner v-else :inline="true" size="16px" color="#ffffff" :text="$t('login.submitLoading')" text-color="#ffffff" />
          </button>

          <span v-if="loginError" class="error-message">{{ loginError }}</span>

          <div class="links-container">
            <div class="forgot-password">
              <a href="#" @click.prevent="showPasswordReset">{{ $t('login.forgotLink') }}</a>
            </div>

            <div class="forgot-password">
              <a :href="signupMailto" target="_blank">{{ $t('login.signupLink') }}</a>
            </div>

            <div class="forgot-password">
              <a href="https://www.bfh.ch/en/research/research-projects/2025-243-987-410/"
                target="_blank">{{ $t('login.homepageLink') }}</a>
            </div>

            <div class="forgot-password">
              <a href="#" @click.prevent="openDialog('datenschutz.html')">{{ $t('login.dataProtectionLink') }}</a> |
              <a href="#" @click.prevent="openDialog('nutzungsbedingungen.html')">{{ $t('login.termsLink') }}</a>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Dialogs -->
    <div v-if="isDialogVisible" class="dialog-overlay" @click="closeDialog">
      <div class="dialog-box" @click.stop>
        <div v-html="dialogContent" class="dialog-content"></div>
        <button @click="closeDialog">{{ $t('login.close') }}</button>
      </div>
    </div>

    <!-- Password Reset Component -->
    <password-reset v-if="isPasswordResetVisible" :username="username" @close="closePasswordReset" @clearError="clearLoginError" />


    <!-- New User Dialog -->
    <div v-if="isNewUserDialogVisible" class="dialog-overlay" @click="closeNewUserDialog">
      <div class="dialog-box" @click.stop>
        <h2>{{ $t('login.newUserDialog.title') }}</h2>
        <p>{{ $t('login.newUserDialog.body') }}</p>
        <button @click="closeNewUserDialog">{{ $t('login.close') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import datenschutzContent from '@/assets/datenschutz.html';
import nutzungsbedingungenContent from '@/assets/nutzungsbedingungen.html';
import PasswordReset from './PasswordReset.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import apiService from '@/services/apiService';

export default {
  name: 'LoginPage',
  components: {
    PasswordReset,
    LoadingSpinner
  },
  data() {
    return {
      username: '',
      password: '',
      loginError: null,
      isLoading: false,
      isPasswordResetVisible: false,
      isDialogVisible: false,
      isNewUserDialogVisible: false,
      isForgotPasswordDialogVisible: false,
      dialogContent: '',
    };
  },
  computed: {
    signupMailto() {
      const subject = encodeURIComponent('SEQ — I request a user account');
      return `mailto:tamara.bucher@bfh.ch?subject=${subject}`;
    },
  },
  methods: {

    clearLoginError() {
      this.loginError = null;
    },
    
    openDialog(htmlFile) {
      if (htmlFile === 'datenschutz.html') {
        this.dialogContent = datenschutzContent;
      } else if (htmlFile === 'nutzungsbedingungen.html') {
        this.dialogContent = nutzungsbedingungenContent;
      }
      this.isDialogVisible = true;
    },

    closeDialog() {
      this.isDialogVisible = false;
    },

    showNewUserDialog() {
      this.isNewUserDialogVisible = true;
    },

    closeNewUserDialog() {
      this.isNewUserDialogVisible = false;
    },

    showForgotPasswordDialog() {
      this.isForgotPasswordDialogVisible = true;
    },

    closeForgotPasswordDialog() {
      this.isForgotPasswordDialogVisible = false;
    },

async login() {
  this.loginError = null;
  this.isLoading = true;

  try {
    console.log('Starting login process...');

    // Use centralized login method from apiService
    const result = await apiService.login(this.username, this.password);

    if (result.success) {
      console.log('Login successful:', result.user);

      // Emit login event
      this.$emit('login');

      // Redirect to homepage or dashboard
      console.log('Login successful, redirecting...');
      this.$router.push('/').catch(error => {
        console.error('Navigation failed:', error);
        // Fallback to direct URL
        window.location.href = '/';
      });
    } else if (result.requiresPasswordReset) {
      // User must reset password - automatically open password reset modal
      this.loginError = this.$t('login.errors.passwordResetRequired');
      this.showPasswordReset();
    } else if (result.accountInactive) {
      // Account is inactive
      this.loginError = this.$t('login.errors.accountInactive');
    } else {
      this.loginError = result.error;
    }
  } catch (error) {
    console.error('Login error:', error);
    this.loginError = this.$t('login.errors.generic');
  } finally {
    this.isLoading = false;
  }
},

    showPasswordReset() {
      this.isPasswordResetVisible = true;
    },

    closePasswordReset() {
      this.isPasswordResetVisible = false;
    }
  }
};
</script>

<style scoped>
#login-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url('/public/login-image.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 0.5rem;
  box-sizing: border-box;
}

.login-content {
  width: 100%;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 0;
}

.main-title {
  color: #4e9e81;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  width: 100%;
  max-width: 90%;
  margin: 0 auto 0.75rem auto;
  padding: 0.75rem;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  line-height: 1.3;
  font-weight: 600;
  box-sizing: border-box;
  text-align: center;
}

.main-title br {
  display: none;
}

.login-container {
  width: 100%;
  max-width: 90%;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
  margin: 0 auto;
  text-align: center;
  box-sizing: border-box;
}

.login-container h2 {
  color: #404040;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
}

.input-container {
  margin-bottom: 0.75rem;
  text-align: left;
}

.input-container label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #505050;
  font-size: 0.75rem;
}

.input-container input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background-color: #ffffff;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  color: #333;
}

.input-container input:hover {
  border-color: #4e9e81;
}

.input-container input:focus {
  border-color: #4e9e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(78, 158, 129, 0.2);
}

button {
  width: 100%;
  padding: 0.5rem;
  background-color: #4e9e81;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.2s ease;
  margin-top: 0.5rem;
}

button:hover:not(:disabled) {
  background-color: #3e8d70;
}

button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.input-container input:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.error-message {
  color: #e05252;
  margin-top: 0.5rem;
  display: block;
  font-size: 0.75rem;
}

.links-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.forgot-password {
  text-align: center;
}

.forgot-password a {
  color: #4e9e81;
  text-decoration: none;
  font-size: 0.75rem;
  transition: color 0.2s ease;
  word-break: break-word;
}

.forgot-password a:hover {
  color: #3e8d70;
  text-decoration: underline;
}

/* Dialog styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 0.5rem;
  box-sizing: border-box;
}

.dialog-box {
  background-color: #fff;
  padding: 1rem;
  border-radius: 8px;
  width: 100%;
  max-width: min(500px, 95%);
  max-height: 80vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  overflow-y: auto;
  box-sizing: border-box;
}

.dialog-content {
  margin-bottom: 0.75rem;
  color: #404040;
  line-height: 1.4;
  font-size: 0.875rem;
}

.dialog-box button {
  background-color: #505050;
  width: auto;
  min-width: 80px;
  padding: 0.4rem 1rem;
  margin: 0.75rem auto 0;
  display: block;
}

.dialog-box button:hover {
  background-color: #3e3e3e;
}

/* Media Queries */
@media screen and (min-height: 700px) {
  .login-content {
    padding: 1rem 0;
  }

  .main-title {
    margin-bottom: 1rem;
    padding: 1rem;
  }

  .login-container {
    padding: 1.25rem;
  }
}

@media screen and (min-width: 481px) and (min-height: 600px) {
  .login-content {
    max-width: 400px;
  }

  .main-title {
    max-width: 400px;
    font-size: 1.2rem;
  }

  .login-container {
    max-width: 400px;
  }

  .input-container label {
    font-size: 0.875rem;
  }

  .input-container input {
    padding: 0.625rem;
  }

  button {
    padding: 0.625rem;
  }

  .forgot-password a {
    font-size: 0.875rem;
  }
}

@media screen and (max-width: 480px) {
  #login-wrapper {
    align-items: flex-start;
    padding-top: 1rem;
  }
}

@media screen and (min-width: 768px) and (min-height: 768px) {
  .login-content {
    max-width: 450px;
  }

  .main-title {
    max-width: 450px;
    font-size: 1.25rem;
    padding: 1rem;
  }

  .login-container {
    max-width: 450px;
    padding: 1.5rem;
  }

  .input-container {
    margin-bottom: 1rem;
  }

  .input-container label {
    font-size: 0.875rem;
  }

  .input-container input {
    padding: 0.75rem;
    font-size: 1rem;
  }

  button {
    padding: 0.75rem;
    font-size: 1rem;
  }

  .links-container {
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .forgot-password a {
    font-size: 0.875rem;
  }
}

@media screen and (min-width: 1024px) and (min-height: 768px) {
  .main-title br {
    display: inline;
  }
}
</style>